import { useMutation } from 'react-query';
import {
  clientsService,
  controlBackdropLoading,
  createCompany,
  editCompany,
  removeCache,
  yardCompaniesService,
} from '../../../services';
import { ImageObjType, CreateCompanyProps } from '../../../types';
import { showToRoles } from '../../../utils';
import { useStoreUser } from '../../../context';

export const useCreateCompanyMutation = () => {
  return useMutation(
    ({
      city,
      companyLegalName,
      contactPersonFirstName,
      contactPersonLastName,
      country,
      companyEmail,
      companyPhoneNumber,
      email,
      phoneNumber,
      streetAddress,
      zipCode,
      name,
      code,
      yardCompanyId,
      yardId,
      category,
      gateOutAllowed,
      blocks,
      messengers,
      belongsTo,
    }: CreateCompanyProps) => {
      let props: any = {
        legalName: companyLegalName,
        address: {
          streetAddress,
          city,
          country,
        },
        email: companyEmail,
        phoneNumber: companyPhoneNumber,
        contactPerson: {
          firstName: contactPersonFirstName,
          lastName: contactPersonLastName,
          email,
          phoneNumber,
        },
        messengers: messengers.filter((item) => !!item.label && !!item.identifier),
      };
      Object.keys(props.contactPerson).forEach((key) => {
        if (!props.contactPerson[key]) delete props.contactPerson[key];
      });

      if (belongsTo === 'CLIENT') {
        props.code = code;
        props.category = category;
        props.gateOutAllowed = gateOutAllowed;
        if (yardId) props.yardId = yardId;
      }
      if (belongsTo === 'YARD') {
        props = {
          name,
          code,
          address: {
            streetAddress,
            city,
            country,
          },
          yardCompanyId,
          messengers: messengers.filter((item) => !!item.label && !!item.identifier),
        };

        if (phoneNumber) props.phoneNumber = phoneNumber;
        if (blocks.length)
          props.blocks = blocks
            .filter((item) => !!item.block && !!item.rowLength && !!item.slotLength)
            .map((item) => ({
              block: item.block,
              rowLength: item.rowLength,
              slotLength: item.slotLength,
            }));
      }

      if (zipCode) props.address.zipCode = zipCode;

      let path = '/yard-companies';
      if (belongsTo === 'YARD') {
        path = '/yards';
      }
      if (belongsTo === 'CLIENT') {
        path = '/clients';
      }

      return createCompany({ path, props });
    },
    {
      onSuccess: () => {
        removeCache(['yard-companies', 'yard', 'client', 'key']);
      },
    },
  );
};

export const useUpdateClientDocumentsMutation = () => {
  return useMutation(
    ({
      id,
      documents = [],
      descriptions,
    }: {
      id: string;
      documents: ImageObjType[];
      descriptions: string[];
    }) => {
      controlBackdropLoading(1);

      return clientsService.updateDocuments({ id, documents, descriptions }).then((res: any) => {
        let docs = [];
        if (typeof res === 'object') docs = res.data.data;

        return docs || [];
      });
    },
    {
      onSettled: () => {
        removeCache(['client']);
        controlBackdropLoading(-1);
      },
    },
  );
};

export const useUpdateYardCompanyLogoMutation = () => {
  return useMutation(
    ({
      id,
      logo,
    }: {
      id: string;
      logo?: { action: 'upload'; file: File } | { action: 'delete'; name: string };
    }) => {
      controlBackdropLoading(1);
      return yardCompaniesService.updateLogo(id, logo);
    },
    {
      onSettled: () => {
        removeCache(['yard-companies']);
        controlBackdropLoading(-1);
      },
    },
  );
};

export const useEditCompanyMutation = ({ id }: { id: string }) => {
  const { user } = useStoreUser();

  return useMutation(
    ({ blocks, belongsTo, blocksScene, blocksTier, code, ...props }: any) => {
      props = {
        canTrade: props.canTrade,
        invoiceCreateMode: props.invoiceCreateMode,
        category: props.category,
        gateOutAllowed: props.gateOutAllowed,
        comment: props.comment,
        messengers: props.messengers.filter((item: any) => !!item.label && !!item.identifier),
        fileNames: props.descriptions,
      };
      if (belongsTo !== 'CLIENT') {
        delete props.category;
        delete props.gateOutAllowed;
        delete props.comment;
        delete props.fileNames;
      }
      if (belongsTo !== 'YARD') {
        delete props.canTrade;
        delete props.invoiceCreateMode;
      }

      let path = '/yard-companies';
      if (belongsTo === 'YARD') {
        props.blocksScene = blocksScene;
        props.blocksTier = Number(blocksTier);
        if (blocks.length)
          props.blocks = blocks
            .filter((item: any) => !!item.block && !!item.rowLength && !!item.slotLength)
            .map((item: any) => ({
              ...(item.id ? { id: item.id } : {}),
              block: item.block,
              rowLength: item.rowLength,
              slotLength: item.slotLength,
            }));

        path = '/yards';
      }
      if (belongsTo === 'CLIENT') {
        if (showToRoles(['SUPER_ADMIN'], user)) props.code = code;

        path = '/clients';
      }
      path = path + `/${id}`;

      return editCompany({ path, props });
    },
    {
      onSuccess: () => {
        removeCache(['yard-companies', 'yard', 'client', 'container', 'key']);
      },
    },
  );
};
